"use client";

import Link from "next/link";
import { Grid2x2, Target, FolderOpen } from "lucide-react";
import { useAuthModal } from "@/lib/context/AuthModalContext";
import { ChromeProvider } from "@/components/chrome/ChromeContext";
import { TopNav } from "@/components/chrome/TopNav";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cellIndex } from "@/lib/domain/grid";

/* ── Decorative matrix data ── */

type CellColor = "raise" | "call" | "call-dim" | "empty";

const RAISE_PAIRS = new Set([0, 1, 2, 3, 4, 5, 6].map((i) => cellIndex(i, i)));
const CALL_SUITED = new Set([
  cellIndex(0, 1),
  cellIndex(0, 2),
  cellIndex(0, 3),
  cellIndex(0, 4),
  cellIndex(1, 2),
]);
const CALL_DIM_PAIRS = new Set([7, 8, 9].map((i) => cellIndex(i, i)));

function cellColor(i: number): CellColor {
  if (RAISE_PAIRS.has(i)) return "raise";
  if (CALL_SUITED.has(i)) return "call";
  if (CALL_DIM_PAIRS.has(i)) return "call-dim";
  return "empty";
}

const CELL_CLASS: Record<CellColor, string> = {
  raise: "bg-raise",
  call: "bg-call",
  "call-dim": "bg-call/50",
  empty: "bg-panel",
};

const MATRIX = Array.from({ length: 169 }, (_, i) => cellColor(i));

/* ── Features data ── */

const FEATURES = [
  {
    icon: Grid2x2,
    title: "Visual range editor",
    body: "Paint hand combinations with a 13×13 matrix. Supports pure and mixed-frequency ranges.",
  },
  {
    icon: Target,
    title: "Preflop trainer",
    body: "Drill your ranges until they’re automatic. Instant feedback on every decision.",
  },
  {
    icon: FolderOpen,
    title: "Range library",
    body: "Organize ranges into folders. Import and export in any format.",
  },
] as const;

/* ── Component ── */

export function HomeClient() {
  const { openAuthModal } = useAuthModal();

  function scrollToFeatures() {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <ChromeProvider>
      <TooltipProvider delayDuration={250}>
      <div className="flex h-full flex-col bg-bg">
        <TopNav />
        <div
          className="flex flex-col"
          style={{ height: "calc(100dvh - 48px)", overflowY: "auto" }}
        >
          {/* ── Hero ── */}
          <section className="relative flex min-h-[calc(100dvh-48px)] flex-col items-center justify-center px-4">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(var(--line) 1px, transparent 1px), linear-gradient(90deg, var(--line) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
                opacity: 0.035,
              }}
            />

            <div className="relative z-10 flex flex-col items-center text-center">
              <span className="caps-mute mb-4">Preflop Range Editor</span>

              <h1
                className="font-bold tracking-tight text-fg"
                style={{ fontSize: "clamp(32px, 5vw, 48px)" }}
              >
                Build. Study. Drill.
              </h1>

              <p className="mt-4 max-w-[480px] text-base text-fg-dim">
                Paint and drill GTO preflop ranges with a professional editor
                built for serious players.
              </p>

              <div className="mt-8 flex gap-3">
                <Button onClick={() => openAuthModal("register")}>
                  Start for free
                </Button>
                <Button variant="ghost" onClick={scrollToFeatures}>
                  See how it works
                </Button>
              </div>

              {/* Mini matrix preview */}
              <div
                className="mt-12 rounded-lg bg-panel-2 p-3.5"
                style={{ boxShadow: "var(--shadow-glow)" }}
              >
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: "repeat(13, 20px)",
                    gridTemplateRows: "repeat(13, 20px)",
                    gap: "2px",
                  }}
                >
                  {MATRIX.map((color, i) => (
                    <div key={i} className={`rounded-sm ${CELL_CLASS[color]}`} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── Features ── */}
          <section id="features" className="px-4 py-20">
            <div className="mx-auto grid max-w-[900px] grid-cols-1 gap-10 md:grid-cols-3 md:gap-0">
              {FEATURES.map((f, idx) => (
                <div
                  key={f.title}
                  className={`flex flex-col items-center text-center md:items-start md:text-left md:px-8 ${
                    idx > 0 ? "md:border-l md:border-line" : ""
                  }`}
                >
                  <f.icon className="h-4 w-4 text-accent" />
                  <h3 className="mt-3 text-sm font-semibold text-fg">
                    {f.title}
                  </h3>
                  <p
                    className="mt-1.5 text-[13px] leading-relaxed text-fg-dim"
                    style={{ maxWidth: "32ch" }}
                  >
                    {f.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ── CTA strip ── */}
          <section className="bg-panel px-4 py-16">
            <div className="mx-auto flex max-w-[480px] flex-col items-center text-center">
              <h2 className="text-2xl font-bold text-fg">
                Ready to improve your preflop game?
              </h2>
              <p className="mt-3 text-sm text-fg-dim">
                Free to start. No credit card required.
              </p>
              <Button
                className="mt-6"
                onClick={() => openAuthModal("register")}
              >
                Get started free
              </Button>
            </div>
          </section>

          {/* ── Footer ── */}
          <footer className="flex h-[26px] items-center justify-between border-t border-line bg-bg px-4">
            <span className="caps-mute" style={{ fontSize: "10px" }}>
              &copy; 2026 PreflopCraft
            </span>
            <nav className="flex items-center gap-4">
              <Link href="/legal/privacy" className="caps-mute" style={{ fontSize: "10px" }}>
                Privacy
              </Link>
              <Link href="/legal/terms" className="caps-mute" style={{ fontSize: "10px" }}>
                Terms
              </Link>
              <span className="caps-mute" style={{ fontSize: "10px" }}>
                v0.1.0
              </span>
            </nav>
          </footer>
        </div>
      </div>
      </TooltipProvider>
    </ChromeProvider>
  );
}
